import { authFetch, type FetchLike } from "./http";
import { API_BASE_URL } from "@/api/base";

/**
 * Who the chrome says you are: your name, and your face beside it.
 *
 * The account chip used to read "Signed in" over `f18a8a25-f5cb-456a-a696-7b06ec10c865`, with an
 * avatar of "F" — the first character of a uuid — while the record two panels away said DEVIN LIAM
 * BUTLER and carried the photograph. The data existed; nothing brought it to the chrome.
 *
 * 🔴 The name is FETCHED, not read out of the session token, because a bearer token is presented to
 * every audience it reaches and a name claim would travel with it.
 *
 * Best-effort by design: a person nobody has named yet, an offline first paint, a photo that 404s —
 * each falls back to what it can draw, and none of them may turn a working sign-in into a failed one.
 */
export interface UserDisplay {
  displayName: string | null;
  handle: string | null;
  hasPhoto: boolean;
}

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() !== "" ? value.trim() : null);

/**
 * 🔴 **THE APPLICATION ANSWERS ABOUT ITS OWN PEOPLE** (D52, D37).
 *
 * This once asked the Passport, which returned `{displayName: null, handle: null}` for anybody who
 * had not filled their own record in — so the footer said "Signed in" over a raw uuid while the Users
 * screen two clicks away said **Devin Butler · devin@metaship.ai**. D37 had already ended the split:
 * there is one `users` row per human, with a name and an e-mail. The Passport fallback outlived the
 * reason for it and went with the app (PNK-106).
 *
 * `GET /users/me` is registered PUBLIC in the scope register — it answers only about the caller and
 * carries nothing about any company — so it needs no organisation and runs on the first paint.
 */
export const DESK_DISPLAY_PATH = "/users/me";

interface DeskUserBody {
  user?: { displayName?: unknown; email?: unknown };
}

export async function fetchDeskDisplay(base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<UserDisplay | null> {
  try {
    const response = await authFetch(`${base}${DESK_DISPLAY_PATH}`, undefined, fetchImpl);
    if (!response.ok) return null;
    const body = (await response.json()) as DeskUserBody;
    const displayName = text(body.user?.displayName);
    const handle = text(body.user?.email);
    // Nothing to say is not an answer — the chip draws its fallback rather than pinning to an empty one.
    if (displayName === null && handle === null) return null;
    // Whether there is a photo is settled by asking for it: `fetchAvatar` 404s cheaply and the chip
    // falls back to the monogram. One less field to keep true in two places.
    return { displayName, handle, hasPhoto: false };
  } catch {
    return null;
  }
}

/** The caller's own likeness (services/user/face.ts). Authenticated, so it is fetched, never an `<img src>`. */
export const PHOTO_PATH = "/me/photo";

/**
 * The photo as an object URL. It has to be FETCHED rather than put in an `<img src>`: the endpoint is
 * authenticated and an `<img>` sends no Authorization header, so a bare src would 401 silently and
 * leave the monogram behind with no clue why.
 *
 * The caller owns the returned URL and must `URL.revokeObjectURL` it when the session ends.
 */
export async function fetchAvatar(base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<string | null> {
  try {
    const response = await authFetch(`${base}${PHOTO_PATH}`, undefined, fetchImpl);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (blob.size === 0) return null;
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}
